
import { isValid, format, formatDuration, parse, intervalToDuration, parseISO, parseJSON as parseJSONDate } from 'date-fns';
import { zeroPadString } from './data-utils';
import { translate } from '../translations';

export const regexIso8601 = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})([.\d]*)Z/;
export const regexJSONDate = /(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})([.\d\+]*)/;


export function isValidDate(value: string): boolean {
  let yearFirst = /^([0-9]{4})([\/-])([0-9]{1,2})([\/-])([0-9]{1,2})/
  let yearLast = /^([0-9]{1,2})([\/-])([0-9]{1,2})([\/-])([0-9]{4})/

  return yearFirst.test(value) || yearLast.test(value) || regexIso8601.test(value);

}

export function parseDate(inValue: any, format?: string): Date {
  if (typeof inValue === 'string') {
    let isISO = regexIso8601.test(inValue);
    let isJSON = regexJSONDate.test(inValue)
    if (isJSON) {
      return parseJSONDate(inValue)
    }
    else if (isISO) {
      return parseISO(inValue)
    } else {
      let dateFormat = format || "yyyy-MM-dd"
      let result = parse(inValue, dateFormat, new Date())
      if (!isValid(result))
        throw new Error(`Unable to parse "${inValue}" to a date using format: ${dateFormat}`);
      return result;
    }

  }
  if (inValue instanceof Date)
    return inValue;
  throw new Error(`Value "${inValue}" cannot be parsed to a date.`);

}

/**
 * Reviver function that converts ISO 8601 strings into Date instances during JSON parsing.
 * @param key Current object key being processed.
 * @param value Value associated with the key.
 * @returns A Date when the value is an ISO string; otherwise the original value.
 */
export const reviveDateTime = (key: any, value: any) => {
  var match;
  if (typeof value === "string" && (match = value.match(regexIso8601))) {
    var milliseconds = Date.parse(match[0]);
    if (!isNaN(milliseconds)) {
      return new Date(milliseconds);
    }
  }
  return value;
}

/**
 * Evaluates whether a string appears to represent a date.
 * @param value Text to inspect for date formatting.
 * @returns True when the string matches common date formats or ISO 8601.
 */
export function isDate(value: string): boolean {
  let yearFirst = /^([0-9]{4})([\/-])([0-9]{1,2})([\/-])([0-9]{1,2})/
  let yearLast = /([0-9]{1,2})([\/-])([0-9]{1,2})([\/-])([0-9]{4})\Z/

  return yearFirst.test(value) || yearLast.test(value) || regexIso8601.test(value);

}

/**
 * Describes which date formats a string matches.
 * @param value Input string potentially containing a date.
 * @returns Object indicating detected formats (year-first, year-last, iso8601).
 */
export function getDateFormat(value: string) {
  let yearFirst = /^([0-9]{4})([\/-])([0-9]{1,2})([\/-])([0-9]{1,2})/
  let yearLast = /([0-9]{1,2})([\/-])([0-9]{1,2})([\/-])([0-9]{4})\Z/
  let iso8601 = regexIso8601.test(value);

  return {
    yearFirst: yearFirst.test(value),
    yearLast: yearLast.test(value),
    iso8601: iso8601
  }

}

/**
 * Determines whether the provided value is an ISO 8601 formatted string.
 * @param value Value to evaluate.
 * @returns True when the value is a string matching the ISO regex.
 */
export function isIsoDateString(value: any): boolean {
  return value && typeof value === "string" && regexIso8601.test(value);
}

/**
 * Recursively walks an object tree converting ISO 8601 strings to Date instances.
 * @param body Object or array to process.
 * @returns The mutated object with ISO strings replaced by Date objects.
 */
export function handleDates(body: any) {
  if (body === null || body === undefined || typeof body !== "object")
    return body;

  for (const key of Object.keys(body)) {
    const value = body[key];
    if (isIsoDateString(value)) {
      var milliseconds = Date.parse(value);
      if (!isNaN(milliseconds)) {
        body[key] = new Date(milliseconds);
      }
    }
    else if (typeof value === "object") handleDates(value);
  }
}

/**
 * Formats the given date as a time string using `HH:mm:ss`.
 * @param date Date instance or ISO string to format.
 * @returns Formatted time string or empty string when no date is supplied.
 */
export function formatTime(date?: Date | string): string {
  if (date) {
    let value: Date;
    if (typeof date === 'string') {
      value = parseISO(date)
    } else {
      value = date;
    }

    return format(value, 'HH:mm:ss');

  }
  return '';
}


/**
 * Formats a date as `yyyy-MM-dd HH:mm:ss` or `yyyy-MM-dd` when `dateOnly` is true.
 * @param date Date instance, ISO string, or null to format.
 * @param dateOnly When true, omits the time portion.
 * @returns Formatted date string or empty string when no date provided.
 */
export function formatDate(date?: Date | string | null, dateOnly?: boolean): string {
  if (date) {
    let value: Date;
    if (typeof date === 'string') {
      value = parseISO(date)
    } else {
      value = date;
    }
    if (!dateOnly)
      return format(value, 'yyyy-MM-dd HH:mm:ss');
    else
      return format(value, 'yyyy-MM-dd');
  }
  return '';
}

/**
 * Formats a date as `yyyy_MM_dd_HH_mm_ss` 
 * @param date Date instance, ISO string, or null to format.
 * @param dateOnly When true, omits the time portion.
 * @returns Formatted date string or empty string when no date provided.
 */
export function dateFileStamp(date?: Date | string | null): string {
  if (date) {
    let value: Date;
    if (typeof date === 'string') {
      value = parseISO(date)
    } else {
      value = date;
    }

    return format(value, 'yyyy_MM_dd_HH_mm_ss');

  }
  return '';
}

/**
 * Formats dates using the Fabrix standard representation.
 * @param date Date instance or ISO string to format.
 * @param dateOnly Optional flag for date-only formatting (unused, maintained for signature compatibility).
 * @returns Formatted date string.
 */
export function formatFbxDate(date?: Date | string | null, dateOnly?: boolean): string {
  return formatDate(date);
}


/**
 * Converts a duration in seconds to a localized human-readable string.
 * @param seconds Duration length in seconds.
 * @returns Human-friendly duration text (e.g., `5 minutes`).
 */
export function humanizeDuration(seconds: number): string {
  let duration = {
    seconds: seconds,

  };
  return formatDuration(duration);


}

/**
 * Displays the elapsed time from a specific date until now, falling back to a standard date after one day.
 * @param date Date instance or ISO string marking the starting point.
 * @returns Relative duration with an `AGO` suffix or a formatted date.
 */
export function showDuration(date?: Date | string): string {
  if (!date) {

    return "";
  }
  if (typeof date === 'string') {
    date = parseISO(date)
  }

  const daysToShow = 24;

  let endDate = new Date();
  let duration = intervalToDuration({ start: date, end: endDate })
  if (!duration.days || duration.days < daysToShow) {
    let fmtStr: string = "";
    if (duration.hours)
      fmtStr = `${fmtStr} ${duration.hours} hrs`;
    if (duration.minutes)
      fmtStr = `${fmtStr} ${duration.minutes} min`;
    if (!duration.hours && !duration.minutes && duration.seconds)
      fmtStr = `${fmtStr} ${duration.seconds} s`;
    return `${fmtStr.trim()} ${translate("AGO")}`;
  } else {
    return formatDate(date);
  }
}

/**
 * Converts a duration specified in minutes to an `HH:mm:ss` string.
 * @param value Duration expressed in minutes, including fractional values.
 * @returns String representing hours, minutes, and seconds.
 */
export function formatDurationAsTime(value: number): string {


  let hours = Math.floor(value / 60);
  let minutes = Math.floor(value - (hours * 60));
  let seconds = ((value - minutes - (hours * 60)) * 60).toFixed(0);

  return `${zeroPadString(2, hours)}:${zeroPadString(2, minutes)}:${zeroPadString(2, seconds)}`
}


/**
 * Converts all Date instances within an object tree to ISO strings.
 * @param obj Object or array potentially containing Date values.
 * @returns Mutated object with Date values serialized to strings.
 */
export function datesToString(obj: any): any {

  if (Array.isArray(obj)) {
    return obj.map(x => datesToString(x));
  }

  if (obj) {
    for (const [key, value] of Object.entries(obj)) {
      if (value instanceof Date) {
        obj[key] = value.toJSON();
      } else if (typeof value === 'object') {
        datesToString(value);
      }
    }

  }
  return obj;
}

